// v3.15-1208 ADMIN-004 — Company Badge System admin.
//
// Recognition subsystem. Configuration-driven. Programs → Badges → Rules →
// (award) → immutable Badge Award Events → replayable projections.
// NOT part of compensation calculations, payroll, or money manager.
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Award, Plus, Trash2, Pencil, Power, PowerOff, RefreshCw, Trophy, Star, Medal, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const ICON_TOKENS = ['medal', 'star', 'trophy', 'shield'];

const iconFor = (tok, cls = 'w-4 h-4') => {
  switch (tok) {
    case 'trophy': return <Trophy className={cls} />;
    case 'star':   return <Star className={cls} />;
    case 'shield': return <ShieldCheck className={cls} />;
    default:       return <Medal className={cls} />;
  }
};

const fmtDate = (iso) => (iso ? iso.slice(0, 10) : '—');

/* ============================== Program modal ============================== */

const ProgramModal = ({ open, onClose, onSaved, program }) => {
  const editing = !!program;
  const [companyId, setCompanyId] = useState(program?.company_id || '');
  const [name, setName]           = useState(program?.name || '');
  const [eff, setEff]             = useState(program?.effective_date || new Date().toISOString().slice(0, 10));
  const [exp, setExp]             = useState(program?.expiration_date || '');
  const [notes, setNotes]         = useState(program?.notes || '');
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    setCompanyId(program?.company_id || '');
    setName(program?.name || '');
    setEff(program?.effective_date || new Date().toISOString().slice(0, 10));
    setExp(program?.expiration_date || '');
    setNotes(program?.notes || '');
  }, [program]);

  const submit = async () => {
    if (!editing && (!companyId || !name)) { toast.error('Company + Name required'); return; }
    if (editing && !name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/badge-system/programs/${program.id}`, {
          name,
          effective_date:  eff  || undefined,
          expiration_date: exp  || undefined,
          notes:           notes || undefined,
        });
        toast.success('Program updated');
      } else {
        await api.post('/badge-system/programs', {
          company_id:      companyId,
          name,
          effective_date:  eff  || undefined,
          expiration_date: exp  || undefined,
          notes:           notes || undefined,
        });
        toast.success('Program created');
      }
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">
            {editing ? 'Edit Badge Program' : 'New Badge Program'}
          </h2>
          {!editing && (
            <>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Company ID</label>
              <Input data-testid="bg-program-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="mb-3" placeholder="UUID" />
            </>
          )}
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Program Name</label>
          <Input data-testid="bg-program-name" value={name} onChange={(e) => setName(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Effective Date</label>
          <Input data-testid="bg-program-eff" type="date" value={eff} onChange={(e) => setEff(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Expiration Date</label>
          <Input data-testid="bg-program-exp" type="date" value={exp} onChange={(e) => setExp(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Notes</label>
          <Input data-testid="bg-program-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="bg-program-save" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Save' : 'Create')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============================== Badge modal ============================== */

const BadgeModal = ({ open, onClose, onSaved, programId, badge }) => {
  const editing = !!badge;
  const [name, setName]     = useState(badge?.name || '');
  const [order, setOrder]   = useState(badge?.order ?? 1);
  const [icon, setIcon]     = useState(badge?.icon || 'medal');
  const [notes, setNotes]   = useState(badge?.notes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(badge?.name || '');
    setOrder(badge?.order ?? 1);
    setIcon(badge?.icon || 'medal');
    setNotes(badge?.notes || '');
  }, [badge]);

  const submit = async () => {
    if (!name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const body = { name, order: Number(order), icon, notes: notes || undefined };
      if (editing) {
        await api.put(`/badge-system/programs/${programId}/badges/${badge.id}`, body);
        toast.success('Badge updated');
      } else {
        await api.post(`/badge-system/programs/${programId}/badges`, body);
        toast.success('Badge added');
      }
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">{editing ? 'Edit Badge' : 'Add Badge'}</h2>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Name</label>
          <Input data-testid="bg-badge-name" value={name} onChange={(e) => setName(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Order</label>
          <Input data-testid="bg-badge-order" type="number" min="1" value={order} onChange={(e) => setOrder(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Icon</label>
          <select data-testid="bg-badge-icon" value={icon} onChange={(e) => setIcon(e.target.value)}
                  className="w-full mb-3 border border-slate-300 rounded-lg px-3 py-2 text-sm">
            {ICON_TOKENS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Notes</label>
          <Input data-testid="bg-badge-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="bg-badge-save" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Save' : 'Add')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============================== Rule modal ============================== */

const RuleModal = ({ open, onClose, onSaved, catalog, programId, badgeId, rule }) => {
  const editing = !!rule;
  const [criteria, setCriteria]     = useState(rule?.criteria || {});
  const [mgrReq, setMgrReq]         = useState(!!rule?.manager_approval_required);
  const [execReq, setExecReq]       = useState(!!rule?.executive_approval_required);
  const [status, setStatus]         = useState(rule?.status || 'active');
  const [notes, setNotes]           = useState(rule?.notes || '');
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    setCriteria(rule?.criteria || {});
    setMgrReq(!!rule?.manager_approval_required);
    setExecReq(!!rule?.executive_approval_required);
    setStatus(rule?.status || 'active');
    setNotes(rule?.notes || '');
  }, [rule]);

  const setCrit = (k, v) => setCriteria((c) => ({ ...c, [k]: v }));
  const clearCrit = (k) => setCriteria((c) => { const n = { ...c }; delete n[k]; return n; });

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        criteria,
        manager_approval_required:   mgrReq,
        executive_approval_required: execReq,
        status,
        notes: notes || undefined,
      };
      if (editing) {
        await api.put(`/badge-system/programs/${programId}/badges/${badgeId}/rules/${rule.id}`, body);
        toast.success('Rule updated');
      } else {
        await api.post(`/badge-system/programs/${programId}/badges/${badgeId}/rules`, body);
        toast.success('Rule added');
      }
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">{editing ? 'Edit Rule' : 'Add Rule'}</h2>
          <div className="mb-3">
            <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Qualification Criteria</label>
            <div className="space-y-2 border rounded-lg p-3 bg-slate-50 max-h-56 overflow-y-auto">
              {(catalog || []).map((k) => (
                <div key={k} className="flex items-center gap-2">
                  <label className="text-xs text-slate-700 min-w-[180px]">{k.replaceAll('_', ' ')}</label>
                  <Input
                    data-testid={`bg-rule-crit-${k}`}
                    value={criteria[k] ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '') clearCrit(k);
                      else setCrit(k, isNaN(Number(v)) ? v : Number(v));
                    }}
                    placeholder="—"
                    className="flex-1 text-xs"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="bg-rule-mgr-req" type="checkbox" checked={mgrReq} onChange={(e) => setMgrReq(e.target.checked)} />
              Manager approval required
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="bg-rule-exec-req" type="checkbox" checked={execReq} onChange={(e) => setExecReq(e.target.checked)} />
              Executive approval required
            </label>
          </div>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Status</label>
          <select data-testid="bg-rule-status" value={status} onChange={(e) => setStatus(e.target.value)}
                  className="w-full mb-3 border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </select>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Notes</label>
          <Input data-testid="bg-rule-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="bg-rule-save" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Save' : 'Add')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============================== Award modal ============================== */

const AwardModal = ({ open, onClose, onDone, programs }) => {
  const [programId, setProgramId] = useState('');
  const [badgeId, setBadgeId]     = useState('');
  const [empId, setEmpId]         = useState('');
  const [mgrId, setMgrId]         = useState('');
  const [execId, setExecId]       = useState('');
  const [snapshot, setSnapshot]   = useState('');
  const [note, setNote]           = useState('');
  const [saving, setSaving]       = useState(false);

  const program = programs.find((p) => p.id === programId);
  const badge   = program?.badges?.find((b) => b.id === badgeId);
  const rules   = (badge?.rules || []).filter((r) => (r.status || 'active') === 'active');
  const needsMgr  = rules.some((r) => r.manager_approval_required);
  const needsExec = rules.some((r) => r.executive_approval_required);

  const run = async () => {
    if (!programId || !badgeId || !empId) { toast.error('Program, badge, and employee required'); return; }
    if (needsMgr && !mgrId) { toast.error('Manager approver ID required'); return; }
    if (needsExec && !execId) { toast.error('Executive approver ID required'); return; }
    setSaving(true);
    try {
      let parsedSnapshot = {};
      if (snapshot.trim()) {
        try { parsedSnapshot = JSON.parse(snapshot); }
        catch { toast.error('Snapshot must be valid JSON'); setSaving(false); return; }
      }
      await api.post('/badge-system/award', {
        badge_program_id:       programId,
        badge_id:               badgeId,
        employee_id:            empId,
        manager_approved_by:    mgrId  || undefined,
        executive_approved_by:  execId || undefined,
        qualification_snapshot: parsedSnapshot,
        note:                   note   || undefined,
      });
      toast.success('Badge awarded');
      onDone?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Award failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">Award Badge</h2>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Program</label>
          <select data-testid="bg-award-program" value={programId} onChange={(e) => { setProgramId(e.target.value); setBadgeId(''); }}
                  className="w-full mb-3 border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">— Select program —</option>
            {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Badge</label>
          <select data-testid="bg-award-badge" value={badgeId} onChange={(e) => setBadgeId(e.target.value)}
                  className="w-full mb-3 border border-slate-300 rounded-lg px-3 py-2 text-sm" disabled={!program}>
            <option value="">— Select badge —</option>
            {(program?.badges || []).sort((a, b) => a.order - b.order).map((b) => (
              <option key={b.id} value={b.id}>{b.order}. {b.name}</option>
            ))}
          </select>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Employee ID</label>
          <Input data-testid="bg-award-emp" value={empId} onChange={(e) => setEmpId(e.target.value)} placeholder="uuid" className="mb-3" />
          {needsMgr && (
            <>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Manager Approver ID (required)</label>
              <Input data-testid="bg-award-mgr" value={mgrId} onChange={(e) => setMgrId(e.target.value)} placeholder="uuid" className="mb-3" />
            </>
          )}
          {needsExec && (
            <>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Executive Approver ID (required)</label>
              <Input data-testid="bg-award-exec" value={execId} onChange={(e) => setExecId(e.target.value)} placeholder="uuid" className="mb-3" />
            </>
          )}
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Qualification Snapshot (JSON, optional)</label>
          <Input data-testid="bg-award-snapshot" value={snapshot} onChange={(e) => setSnapshot(e.target.value)} placeholder='{"production_ytd":250000}' className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Note</label>
          <Input data-testid="bg-award-note" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="bg-award-run" onClick={run} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Award'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============================== Page ============================== */

export default function BadgeSystemPage() {
  const [loading, setLoading]     = useState(true);
  const [programs, setPrograms]   = useState([]);
  const [events, setEvents]       = useState([]);
  const [catalog, setCatalog]     = useState([]);
  const [tab, setTab]             = useState('programs');
  const [error, setError]         = useState(null);
  const [newProgram, setNewProgram]   = useState(false);
  const [editProgram, setEditProgram] = useState(null);
  const [newBadgeFor, setNewBadgeFor] = useState(null);
  const [editBadge, setEditBadge]     = useState(null);   // {programId, badge}
  const [newRuleFor, setNewRuleFor]   = useState(null);   // {programId, badgeId}
  const [editRule, setEditRule]       = useState(null);   // {programId, badgeId, rule}
  const [awardOpen, setAwardOpen]     = useState(false);
  const [replaying, setReplaying]     = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [p, e, c] = await Promise.all([
        api.get('/badge-system/programs'),
        api.get('/badge-system/events',  { params: { limit: 500 } }),
        api.get('/badge-system/criteria-catalog'),
      ]);
      setPrograms(p?.data?.items || []);
      setEvents(e?.data?.items || []);
      setCatalog(c?.data?.criteria || []);
    } catch (err) {
      if (err?.response?.status === 403) setError('Badge administration is restricted to admins.');
      else toast.error('Failed to load badge data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const togglePlan = async (p) => {
    const next = (p.status || 'active') === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/badge-system/programs/${p.id}`, { status: next });
      toast.success(`Program ${next === 'active' ? 'activated' : 'deactivated'}`); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const delProgram = async (p) => {
    if (!window.confirm(`Delete program "${p.name}"? This is irreversible.`)) return;
    try { await api.delete(`/badge-system/programs/${p.id}`); toast.success('Program deleted'); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const toggleBadge = async (programId, b) => {
    const next = (b.status || 'active') === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/badge-system/programs/${programId}/badges/${b.id}`, { status: next });
      toast.success(`Badge ${next === 'active' ? 'activated' : 'deactivated'}`); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const delBadge = async (programId, b) => {
    if (!window.confirm(`Delete badge "${b.name}"?`)) return;
    try { await api.delete(`/badge-system/programs/${programId}/badges/${b.id}`); toast.success('Badge removed'); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const toggleRule = async (programId, badgeId, r) => {
    const next = (r.status || 'active') === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/badge-system/programs/${programId}/badges/${badgeId}/rules/${r.id}`, { status: next });
      toast.success(`Rule ${next === 'active' ? 'activated' : 'deactivated'}`); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const delRule = async (programId, badgeId, ruleId) => {
    try { await api.delete(`/badge-system/programs/${programId}/badges/${badgeId}/rules/${ruleId}`); toast.success('Rule removed'); fetchAll(); }
    catch { toast.error('Delete failed'); }
  };

  const replay = async () => {
    setReplaying(true);
    try {
      const { data } = await api.post('/badge-system/replay');
      toast.success(`Replay complete — scanned ${data.scanned}, posted ${data.posted}, skipped ${data.skipped}`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Replay failed'); }
    finally { setReplaying(false); }
  };

  return (
    <div className="space-y-4" data-testid="badge-system-page">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Award className="w-6 h-6 text-amber-600" />
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Badge System</h1>
            <p className="text-sm text-slate-500">
              Recognition subsystem — configuration-driven. Immutable Badge Award
              Events project into a replayable Employee Badge ledger.
              <span className="ml-1 text-slate-400">Not part of compensation, payroll, or money manager.</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="bg-replay-btn" onClick={replay} disabled={replaying} variant="outline">
            {replaying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Replay Projection
          </Button>
          <Button data-testid="bg-award-open" onClick={() => setAwardOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Award className="w-4 h-4 mr-2" /> Award Badge
          </Button>
          <Button data-testid="bg-new-program-btn" onClick={() => setNewProgram(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> New Program
          </Button>
        </div>
      </div>

      {error ? (
        <div className="p-6 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl">{error}</div>
      ) : loading ? (
        <div className="p-10 flex items-center justify-center text-slate-500 bg-white rounded-2xl border">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1 border-b border-slate-200">
            {['programs', 'events'].map((t) => (
              <button key={t} data-testid={`bg-tab-${t}`} onClick={() => setTab(t)}
                      className={
                        'px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ' +
                        (tab === t ? 'border-amber-600 text-amber-700' : 'border-transparent text-slate-500 hover:text-slate-800')
                      }>{t === 'programs' ? 'Programs & Badges' : 'Award Events'}</button>
            ))}
          </div>

          {tab === 'programs' ? (
            programs.length === 0 ? (
              <div className="p-10 text-center text-slate-500 bg-white border rounded-2xl" data-testid="bg-programs-empty">
                No badge programs yet.
              </div>
            ) : (
              <div className="space-y-4">
                {programs.map((p) => {
                  const active = (p.status || 'active') === 'active';
                  return (
                    <div key={p.id} data-testid={`bg-program-${p.id}`} className="bg-white rounded-2xl border border-slate-200 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-bold text-slate-800">{p.name}</h3>
                            <span className={
                              'text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ' +
                              (active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600')
                            }>{p.status}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Company {p.company_id?.slice(0, 8)}… · Effective {fmtDate(p.effective_date)}
                            {p.expiration_date ? ` · Expires ${fmtDate(p.expiration_date)}` : ''}
                          </p>
                          {p.notes && <p className="text-xs text-slate-500 mt-1">{p.notes}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" data-testid={`bg-edit-program-${p.id}`} onClick={() => setEditProgram(p)} variant="outline">
                            <Pencil className="w-4 h-4 mr-1" /> Edit
                          </Button>
                          <Button size="sm" data-testid={`bg-toggle-program-${p.id}`} onClick={() => togglePlan(p)} variant="outline">
                            {active ? <><PowerOff className="w-4 h-4 mr-1" /> Deactivate</> : <><Power className="w-4 h-4 mr-1" /> Activate</>}
                          </Button>
                          <Button size="sm" data-testid={`bg-add-badge-${p.id}`} onClick={() => setNewBadgeFor(p.id)} variant="outline">
                            <Plus className="w-4 h-4 mr-1" /> Add Badge
                          </Button>
                          <button data-testid={`bg-del-program-${p.id}`} onClick={() => delProgram(p)}
                                  className="text-rose-500 hover:text-rose-700 p-2"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>

                      {(p.badges || []).length === 0 ? (
                        <div className="mt-3 text-xs text-slate-400">No badges yet.</div>
                      ) : (
                        <div className="mt-3 space-y-2">
                          {[...p.badges].sort((a, b) => a.order - b.order).map((b) => {
                            const bActive = (b.status || 'active') === 'active';
                            return (
                              <div key={b.id} data-testid={`bg-badge-${b.id}`}
                                   className="border border-slate-100 rounded-xl p-3 bg-slate-50/60">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-white text-amber-600 border">
                                      {iconFor(b.icon)}
                                    </span>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="font-bold text-slate-800">{b.order}. {b.name}</span>
                                        <span className={
                                          'text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ' +
                                          (bActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600')
                                        }>{b.status}</span>
                                      </div>
                                      {b.notes && <div className="text-xs text-slate-500">{b.notes}</div>}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button data-testid={`bg-edit-badge-${b.id}`} onClick={() => setEditBadge({ programId: p.id, badge: b })}
                                            className="text-slate-500 hover:text-slate-800 p-1" title="Edit badge">
                                      <Pencil className="w-4 h-4" />
                                    </button>
                                    <button data-testid={`bg-toggle-badge-${b.id}`} onClick={() => toggleBadge(p.id, b)}
                                            className="text-slate-500 hover:text-slate-800 p-1"
                                            title={bActive ? 'Deactivate' : 'Activate'}>
                                      {bActive ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                                    </button>
                                    <button data-testid={`bg-add-rule-${b.id}`} onClick={() => setNewRuleFor({ programId: p.id, badgeId: b.id })}
                                            className="text-slate-500 hover:text-slate-800 p-1" title="Add rule">
                                      <Plus className="w-4 h-4" />
                                    </button>
                                    <button data-testid={`bg-del-badge-${b.id}`} onClick={() => delBadge(p.id, b)}
                                            className="text-rose-500 hover:text-rose-700 p-1" title="Delete badge">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                                {(b.rules || []).length > 0 && (
                                  <table className="w-full text-xs mt-2">
                                    <thead className="text-slate-500 uppercase text-[10px] tracking-wider">
                                      <tr>
                                        <th className="text-left px-2 py-1">Criteria</th>
                                        <th className="text-center px-2 py-1">Mgr Approval</th>
                                        <th className="text-center px-2 py-1">Exec Approval</th>
                                        <th className="text-center px-2 py-1">Status</th>
                                        <th className="text-right px-2 py-1"></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {b.rules.map((r) => {
                                        const rActive = (r.status || 'active') === 'active';
                                        return (
                                          <tr key={r.id} data-testid={`bg-rule-${r.id}`} className="border-t border-slate-100">
                                            <td className="px-2 py-1 text-slate-700">
                                              {Object.keys(r.criteria || {}).length === 0
                                                ? <span className="text-slate-400">—</span>
                                                : Object.entries(r.criteria).map(([k, v]) => (
                                                    <span key={k} className="inline-block bg-white border rounded px-1.5 py-0.5 mr-1 mb-1">
                                                      <span className="text-slate-500">{k}:</span> {String(v)}
                                                    </span>
                                                  ))}
                                            </td>
                                            <td className="px-2 py-1 text-center">{r.manager_approval_required ? '✓' : '—'}</td>
                                            <td className="px-2 py-1 text-center">{r.executive_approval_required ? '✓' : '—'}</td>
                                            <td className="px-2 py-1 text-center">
                                              <span className={
                                                'inline-block text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-full ' +
                                                (rActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600')
                                              }>{r.status}</span>
                                            </td>
                                            <td className="px-2 py-1 text-right">
                                              <div className="flex items-center justify-end gap-1">
                                                <button data-testid={`bg-edit-rule-${r.id}`} onClick={() => setEditRule({ programId: p.id, badgeId: b.id, rule: r })}
                                                        className="text-slate-500 hover:text-slate-800 p-0.5"><Pencil className="w-3.5 h-3.5" /></button>
                                                <button data-testid={`bg-toggle-rule-${r.id}`} onClick={() => toggleRule(p.id, b.id, r)}
                                                        className="text-slate-500 hover:text-slate-800 p-0.5">
                                                  {rActive ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                                                </button>
                                                <button data-testid={`bg-del-rule-${r.id}`} onClick={() => delRule(p.id, b.id, r.id)}
                                                        className="text-rose-500 hover:text-rose-700 p-0.5"><Trash2 className="w-3.5 h-3.5" /></button>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
              {events.length === 0 ? (
                <div className="p-10 text-center text-slate-500" data-testid="bg-events-empty">No award events yet.</div>
              ) : (
                <table className="w-full text-sm" data-testid="bg-events-table">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2">Awarded</th>
                      <th className="text-left px-3 py-2">Employee</th>
                      <th className="text-left px-3 py-2">Program</th>
                      <th className="text-left px-3 py-2">Badge</th>
                      <th className="text-left px-3 py-2">Approvers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(e.awarded_at)}</td>
                        <td className="px-3 py-2">{e.employee_name || e.employee_id}</td>
                        <td className="px-3 py-2">{e.badge_program_name}</td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
                            {iconFor(e.badge_icon, 'w-3.5 h-3.5')}
                            {e.badge_order}. {e.badge_name}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {e.manager_approver_email && <div>Mgr: {e.manager_approver_email}</div>}
                          {e.executive_approver_email && <div>Exec: {e.executive_approver_email}</div>}
                          {!e.manager_approver_email && !e.executive_approver_email && '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      <ProgramModal open={newProgram}   onClose={() => setNewProgram(false)}   onSaved={fetchAll} program={null} />
      <ProgramModal open={!!editProgram} onClose={() => setEditProgram(null)}  onSaved={fetchAll} program={editProgram} />
      <BadgeModal   open={!!newBadgeFor} onClose={() => setNewBadgeFor(null)}  onSaved={fetchAll} programId={newBadgeFor} badge={null} />
      <BadgeModal   open={!!editBadge}   onClose={() => setEditBadge(null)}    onSaved={fetchAll} programId={editBadge?.programId} badge={editBadge?.badge} />
      <RuleModal    open={!!newRuleFor}  onClose={() => setNewRuleFor(null)}   onSaved={fetchAll} catalog={catalog} programId={newRuleFor?.programId} badgeId={newRuleFor?.badgeId} rule={null} />
      <RuleModal    open={!!editRule}    onClose={() => setEditRule(null)}     onSaved={fetchAll} catalog={catalog} programId={editRule?.programId}   badgeId={editRule?.badgeId}   rule={editRule?.rule} />
      <AwardModal   open={awardOpen}     onClose={() => setAwardOpen(false)}   onDone={fetchAll} programs={programs} />
    </div>
  );
}
